import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { validateGeoLocation } from '../services/api';
import { SessionContext } from '../contexts/SessionContext';
import { getTranslations } from '../utils/translations';
import '../styles/GeoValidation.css';

// Clave para almacenar las coordenadas en localStorage
const COORDINATES_STORAGE_KEY = 'doorAccessCoordinates';

const GeoValidationPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('');
  const { houseId, setGeoValidated } = useContext(SessionContext);
  const navigate = useNavigate();
  
  // Obtener traducciones según el idioma del navegador
  const translations = getTranslations();

  useEffect(() => {
    // Mostrar mensaje con la imagen de la casa
    if (houseId) {
      setMessage(translations.verifyingLocation);
    }
  }, [houseId, translations]);

  const handleGetLocation = () => {
    setLoading(true);
    setError(null);

    if (!navigator.geolocation) {
      setError(translations.geolocationNotSupported);
      setLoading(false);
      return;
    }

    const handleSuccess = async (position: GeolocationPosition) => {
      try {
        const { latitude, longitude } = position.coords;

        // Guardar las coordenadas en localStorage para usarlas más tarde en /open
        try {
          localStorage.setItem(COORDINATES_STORAGE_KEY, JSON.stringify({ latitude, longitude }));
          console.log('Coordenadas guardadas en localStorage:', { latitude, longitude });
        } catch (storageError) {
          console.error('Error al guardar coordenadas en localStorage:', storageError);
        }

        const response = await validateGeoLocation(latitude, longitude);

        if (response.success) {
          setGeoValidated(true);
          navigate('/door-control');
        } else {
          setError(response.message || translations.codeValidationError);
        }
      } catch (err) {
        setError(translations.connectionError);
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    // Traduce el código de error de geolocalización a un mensaje claro
    const getGeoErrorMessage = (geoError: GeolocationPositionError): string => {
      switch (geoError.code) {
        case geoError.PERMISSION_DENIED:
          return translations.locationPermissionDenied;
        case geoError.POSITION_UNAVAILABLE:
          return translations.locationUnavailable;
        case geoError.TIMEOUT:
          return translations.locationTimeout;
        default:
          return translations.locationAccessError;
      }
    };

    // Segundo intento: precisión baja (Wi-Fi/red), más tiempo y acepta una posición reciente
    const tryLowAccuracy = () => {
      navigator.geolocation.getCurrentPosition(
        handleSuccess,
        (geoError) => {
          console.error('Error de geolocalización (baja precisión):', geoError);
          setError(getGeoErrorMessage(geoError));
          setLoading(false);
        },
        {
          enableHighAccuracy: false,
          timeout: 30000,
          maximumAge: 60000
        }
      );
    };

    // Primer intento: alta precisión (GPS). Si expira, reintenta con baja precisión.
    navigator.geolocation.getCurrentPosition(
      handleSuccess,
      (geoError) => {
        console.error('Error de geolocalización (alta precisión):', geoError);
        if (geoError.code === geoError.TIMEOUT || geoError.code === geoError.POSITION_UNAVAILABLE) {
          // El GPS no consiguió fijar posición a tiempo: reintentar con un método más rápido
          tryLowAccuracy();
        } else {
          setError(getGeoErrorMessage(geoError));
          setLoading(false);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  return (
    <div className="geo-validation-container">
      <div className="geo-validation-card">
        <h1>{translations.locationVerification}</h1>
        
        {houseId && (
          <div className="house-image-container">
            <img 
              src={`/images/house${houseId}.jpeg`} 
              alt={`${translations.houseImage} ${houseId}`} 
              className="house-image"
            />
          </div>
        )}
        
        <p className="instruction-text">{message}</p>
        
        {error && <div className="error-message">{error}</div>}
        
        <button 
          onClick={handleGetLocation} 
          className="proceed-button"
          disabled={loading}
        >
          {loading ? translations.verifying : translations.proceed}
        </button>
      </div>
    </div>
  );
};

export default GeoValidationPage;
